// @ts-strict-ignore
import { useTheme } from "@emotion/react";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { colorProfileMapper } from "aviary-tokens";

import { Pill, Tooltip } from "@aviary/components";
import { useProductAvailabilityLabel } from "@aviary/hooks";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import {
  AvailabilityStatus,
  type AvailabilityStatusValueType as AvailabilityPillStatus,
} from "@shared/types/availabilityStatus";

import type { ProductImageSize } from "../ProductImage.types";

import * as styles from "./AvailabilityPill.styles";

interface Props {
  /**
   * Availability status of the product
   */
  availabilityStatus: AvailabilityPillStatus;
  /**
   * Size of the ProductImage. Determines if Pill or icon is rendered
   */
  size: ProductImageSize;
  /**
   * Expected return date for backordered/out of stock products. Displayed as a tooltip.
   */
  expectedReturnDate?: string;
}

const AvailabilityPill = ({ availabilityStatus, size, expectedReturnDate, ...rest }: Props) => {
  const { availabilityLabelText, expectedReturnDateText } = useProductAvailabilityLabel(
    availabilityStatus,
    expectedReturnDate
  );
  const currentTheme = useTheme();
  const themeColors = colorProfileMapper(currentTheme);

  const getIntention = () => {
    switch (availabilityStatus) {
      case AvailabilityStatus.DISCONTINUED:
      case AvailabilityStatus.UNAVAILABLE:
      case AvailabilityStatus.NA:
        return "danger";
      case AvailabilityStatus.BACKORDERED:
        return "warning";
      case AvailabilityStatus.TO_BE_DISCONTINUED:
        return "system";
      case AvailabilityStatus.FULLY_RESTRICTED:
        return "system";
      default:
        return "";
    }
  };

  const intention = getIntention();

  // TODO: get more info on "New", "Not in catalog", "Vendor backorder" statuses

  if (!intention) {
    return null;
  }

  if (size === "xxsmall" || size === "xsmall") {
    return (
      <Tooltip
        tooltipContent={`${availabilityLabelText}. ${expectedReturnDateText}`}
        css={styles.iconTooltip}
        {...rest}
      >
        <FontAwesomeIcon
          icon={faCircleExclamation}
          title={availabilityLabelText}
          aria-label={availabilityLabelText}
          css={[styles.icon.base, styles.icon.intention(themeColors[intention])]}
        />
      </Tooltip>
    );
  }

  const renderPillSize = () => {
    if (size === "small") {
      return "xsmall";
    }

    if (size === "medium") {
      if (availabilityStatus === AvailabilityStatus.TO_BE_DISCONTINUED) {
        return "xsmall";
      }

      return "small";
    }

    if (size === "large" && availabilityStatus === AvailabilityStatus.TO_BE_DISCONTINUED) {
      return "small";
    }

    return "medium";
  };

  return (
    <Tooltip tooltipContent={expectedReturnDateText} {...rest}>
      <Pill
        isColor={intention}
        size={renderPillSize()}
        data-testid="AvailabilityPill"
        css={styles.pill}
      >
        {availabilityLabelText}
      </Pill>
    </Tooltip>
  );
};

export { AvailabilityPill, type AvailabilityPillStatus };
